<template>
  <div class="iq-ai-response">
    <div class="iq-ai-response-icon-wrapper">
      <div class="">
        <Icon name="iq_large_icon" />
      </div>
    </div>
    <div class="iq-ai-response-content">
      <!-- Thinking/Reasoning Dropdown (Gemini-style) -->
      <IQThinkingDropdown
        v-if="responseData.reasoning || isThinking"
        :reasoning="responseData.reasoning || currentReasoning"
        :isThinking="isThinking"
        :autoExpand="true"
      />
      
      <!-- Main Response Text -->
      <div v-if="!isThinking || responseData.answer" class="iq-ai-response-text" v-html="formattedAnswer"></div>
      
      <!-- Action Buttons Section -->
      <div v-if="responseData.actions && responseData.actions.length > 0" class="actions-section">
        <button 
          v-for="(action, index) in responseData.actions" 
          :key="index" 
          class="action-button"
          @click="handleActionClick(action)"
        >
          {{ action.label }}
        </button>
      </div>
      
      <!-- Sources Section -->
      <div v-if="responseData.sources && responseData.sources.length > 0" class="sources-section">
        <h4 class="sources-heading">Sources</h4>
        <ol class="sources-list">
          <li v-for="(source, index) in responseData.sources" :key="index" class="source-item">
            <a 
              :href="source.source" 
              target="_blank" 
              rel="noopener noreferrer"
              class="source-link"
            >
              <div class="source-icon">
                <Icon name="document_icon" />
              </div>
              <span class="source-title">{{ source.title }}</span>
              <div class="external-link-icon">
                <Icon name="external_link" />
              </div>
            </a>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from "@components/icons/Icon.vue";
import IQThinkingDropdown from "@components/IQThinkingDropdown/IQThinkingDropdown.vue";

const props = defineProps({
  response: {
    type: [String, Object],
    default: "",
  },
  /**
   * Whether the AI is currently in "thinking" phase (before generating response)
   */
  isThinking: {
    type: Boolean,
    default: false
  },
  /**
   * Current reasoning text being streamed during thinking phase
   */
  currentReasoning: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['actionClick']);

// Computed property to handle both string and object response formats
const responseData = computed(() => {
  if (typeof props.response === 'string') {
    return { answer: props.response, sources: [], actions: [], reasoning: '' };
  }
  return props.response || { answer: '', sources: [], actions: [], reasoning: '' };
});

// Format answer text with markdown-like styling
const formattedAnswer = computed(() => {
  const rawAnswer = responseData.value.answer || props.response || '';
  if (!rawAnswer) return '';
  
  let text = String(rawAnswer);
  
  // Clean up any remaining raw dict patterns that might have slipped through
  // Remove patterns like {'type': 'text', 'text': '', 'extras': {...}, 'index': 0}
  text = text.replace(/\{'type':\s*'text',\s*'text':\s*'[^']*',\s*'extras':\s*\{[^}]*\},\s*'index':\s*\d+\}/g, '');
  // Remove patterns like {'type': 'text', 'text': '', 'index': 0}
  text = text.replace(/\{'type':\s*'text',\s*'text':\s*'',\s*'index':\s*\d+\}/g, '');
  // Remove any remaining signature blocks
  text = text.replace(/'signature':\s*'[^']*'/g, '');
  text = text.replace(/'extras':\s*\{[^}]*\}/g, '');
  
  // Escape HTML first to prevent XSS
  text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  
  // Convert markdown bold **text** to <strong>
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // Convert markdown italic *text* to <em> (but not if it's part of a list item)
  text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  
  // Convert bullet points (lines starting with * )
  text = text.replace(/^\* (.+)$/gm, '<li>$1</li>');
  
  // Wrap consecutive <li> elements in <ul>
  text = text.replace(/(<li>.*<\/li>\n?)+/g, '<ul class="answer-list">$&</ul>');
  
  // Convert line breaks
  text = text.replace(/\n\n/g, '</p><p>');
  text = text.replace(/\n/g, '<br>');
  
  // Wrap in paragraph
  if (!text.startsWith('<')) {
    text = `<p>${text}</p>`;
  }
  
  return text;
});

const handleActionClick = (action) => {
  emit('actionClick', action);
};
// Expose responseData for testing
defineExpose({
  responseData,
});
</script>

<style scoped>
.iq-ai-response {
  display: flex;
  gap: 16px;
  width: 100%;
  background: #212121;
  border-radius: 24px;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100%;
}
 
.iq-ai-response-icon-wrapper {
  flex-shrink: 0;
}

.iq-ai-response-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.iq-ai-response-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0%;
  color: #d9d9d9;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  margin: 0;
  max-width: 100%;
  overflow-x: hidden;
}

.iq-ai-response-text :deep(p) {
  margin: 0 0 12px 0;
}

.iq-ai-response-text :deep(p:last-child) {
  margin-bottom: 0;
}

.iq-ai-response-text :deep(strong) {
  font-weight: 600;
  color: #ffffff;
}

.iq-ai-response-text :deep(em) {
  font-style: italic;
}

.iq-ai-response-text :deep(ul.answer-list) {
  margin: 8px 0;
  padding-left: 20px;
  list-style: none;
}

.iq-ai-response-text :deep(ul.answer-list li) {
  position: relative;
  padding-left: 12px;
  margin-bottom: 6px;
}

.iq-ai-response-text :deep(ul.answer-list li::before) {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

/* Sources Section Styles */
.sources-section {
  margin-top: 8px;
}

.sources-heading {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #3b82f6;
  margin: 0 0 16px 0;
}

.sources-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  counter-reset: source-counter;
}

.source-item {
  counter-increment: source-counter;
  display: flex;
  align-items: center;
  gap: 12px;
}

.source-item::before {
  content: counter(source-counter) ".";
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #d9d9d9;
  min-width: 20px;
}

.source-link {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  transition: opacity 0.2s ease;
}

.source-link:hover {
  opacity: 0.8;
}

.source-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.source-title {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #3b82f6;
  text-decoration: underline;
}

.external-link-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  margin-left: auto;
}

/* Icon styling for external link */
.external-link-icon :deep(svg) {
  width: 12px;
  height: 12px;
  fill: #3b82f6;
}

/* Icon styling for document icon */
.source-icon :deep(svg) {
  width: 16px;
  height: 16px;
  fill: #3b82f6;
}

/* Action Buttons Section */
.actions-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-button {
  background: #FFFFFF;
  color: #000000;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
}

.action-button:hover {
  background: #F5F5F5;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.action-button:active {
  transform: scale(0.98);
}
</style>
